import {
    AfterViewInit,
    Component,
    ComponentMirror,
    ComponentRef,
    DestroyRef,
    ElementRef,
    EnvironmentInjector,
    EventEmitter,
    InjectionToken,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Provider,
    Type,
    ViewContainerRef,
    inject,
    reflectComponentType,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IConstant } from '@frontend/oxygen/shared-utils';
import * as _ from 'underscore';

import { IGATrackingModel } from '../../../core/models/gtm.event.model';
import { ISwitcherConfig } from '../../../core/models/switcher-config.model';
import { ITab } from '../tabsPanel/tabs-panel.model';
import { type DefaultSwitchersComponent } from './default.switchers.component';

export const SwitchersComponentToken = new InjectionToken<DefaultSwitchersComponent & Type<any>>('SwitchersComponentToken');

export function provideSwitchersComponent(cmp: typeof DefaultSwitchersComponent): Provider {
    return {
        provide: SwitchersComponentToken,
        useValue: cmp,
    } as Provider;
}

@Component({
    selector: 'switchers',
    template: '<ng-container #baseRef></ng-container>',
    standalone: true,
})
export class SwitchersComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Output() readonly switchAction?: EventEmitter<{ id: string; tab: ITab | {} }> = new EventEmitter();

    @Input() switchIdPropertyName?: string;
    @Input() type: string;
    @Input() switchers: ISwitcherConfig[] | ITab[] | any;
    @Input() filter: any;
    @Input() activeTab: ITab | IConstant;
    @Input() noPaddings: boolean;
    @Input() noMargin: boolean;
    @Input() detectGTMLocation?: string;
    @Input() preventRouteChange?: boolean;
    @Input() GATrackingModule?: string;
    @Input() GTMTrackingObj?: IGATrackingModel;
    @Input() isOverlay?: boolean = false;
    @Input() preventReload?: boolean;
    @Input() sportName?: string;
    @Input() preBuilt?: string;

    private injector = inject(Injector);
    private envInjector = inject(EnvironmentInjector);
    private destroyRef = inject(DestroyRef);
    private readonly vcr = viewChild.required('baseRef', { read: ViewContainerRef });
    private cmpToken = inject(SwitchersComponentToken);

    private cmpRef?: ComponentRef<any>;

    private compMetaData: ComponentMirror<any> | null;

    constructor(private elem: ElementRef) {
        this.compMetaData = reflectComponentType(this.cmpToken);
    }

    ngOnChanges() {
        this.syncInputs();
    }

    ngAfterViewInit() {
        if (!this.cmpToken) {
            throw new Error('Please provide a switchers component!');
        }

        this.cmpRef = this.vcr().createComponent(this.cmpToken, {
            injector: this.injector,
            environmentInjector: this.envInjector,
        });

        this.syncInputs();
        this.syncOutputs();
    }

    private syncInputs() {
        if (this.cmpRef && this.compMetaData?.inputs) {
            this.compMetaData.inputs.forEach((input: IConstant) => {
                this.cmpRef?.setInput(input.propName, (this as any)[input.propName]);
            });

            const dataCrlat = this.elem?.nativeElement?.getAttribute('data-crlat') || this.elem?.nativeElement?.getAttribute('host-data-crlat') || '';
            const hostClasses = this.elem?.nativeElement?.className || this.elem?.nativeElement?.getAttribute('host-classes') || '';
            this.elem.nativeElement.setAttribute('host-classes', hostClasses);
            this.elem.nativeElement.setAttribute('host-data-crlat', dataCrlat);
            this.cmpRef.setInput('dataCrlat', dataCrlat);
            this.cmpRef.setInput('hostClasses', hostClasses);

            this.elem.nativeElement.removeAttribute('class');
            this.elem.nativeElement.removeAttribute('data-crlat');
        }
    }

    private syncOutputs() {
        if (this.compMetaData?.outputs) {
            this.compMetaData.outputs.forEach((output: IConstant) => {
                const outputAction = output.propName;
                this.cmpRef?.instance[outputAction].pipe(takeUntilDestroyed(this.destroyRef)).subscribe((x: any) => {
                    (this as any)[outputAction].emit(x);
                });
            });
        }
    }

    ngOnDestroy(): void {
        this.cmpRef && this.cmpRef.destroy();
    }
}
